import { useCallback, useMemo } from "react";

import {
	settingsActions,
	useSettingsDispatch,
	useSettingsState,
} from "@/context/settings";
import { ContrastModes, SettingsContextType } from "@/types/settings";
import { captureColorThemeChanged } from "@/utils/theme-analytics";

/**
 * Convenience hook that combines state and dispatch with helper functions.
 * This maintains backward compatibility with the existing API.
 *
 * For more granular control, use useSettingsState() and useSettingsDispatch() separately.
 */
export const useSettings = (): SettingsContextType => {
	const state = useSettingsState();
	const dispatch = useSettingsDispatch();

	// Helper functions that dispatch actions
	const setContrastMode = useCallback(
		(contrastMode: ContrastModes) => {
			captureColorThemeChanged({
				newTheme: contrastMode,
				previousTheme: state.contrastMode,
			});
			dispatch(settingsActions.updateContrastMode(contrastMode));
		},
		[dispatch, state.contrastMode],
	);

	const toggleContrastMode = useCallback(() => {
		const currentMode = state.contrastMode;
		let nextMode: ContrastModes;

		if (currentMode === ContrastModes.LIGHT) {
			nextMode = ContrastModes.DARK;
		} else if (currentMode === ContrastModes.DARK) {
			nextMode = ContrastModes.LIGHT;
		} else {
			// If system, toggle based on current system preference
			const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
				.matches
				? ContrastModes.DARK
				: ContrastModes.LIGHT;
			nextMode =
				systemTheme === ContrastModes.DARK
					? ContrastModes.LIGHT
					: ContrastModes.DARK;
		}

		setContrastMode(nextMode);
	}, [setContrastMode, state.contrastMode]);

	const setIsSettingsModalOpen = useCallback(
		(isSettingsModalOpen: boolean) => {
			dispatch(
				settingsActions.updateSettingsModalVisibility(isSettingsModalOpen),
			);
		},
		[dispatch],
	);

	const toggleSettingsModalVisibility = useCallback(() => {
		dispatch(
			settingsActions.updateSettingsModalVisibility(!state.isSettingsModalOpen),
		);
	}, [state.isSettingsModalOpen, dispatch]);

	return useMemo(
		() => ({
			contrastMode: state.contrastMode,
			isSettingsModalOpen: state.isSettingsModalOpen,
			setContrastMode,
			setIsSettingsModalOpen,
			toggleContrastMode,
			toggleSettingsModalVisibility,
		}),
		[
			state.contrastMode,
			state.isSettingsModalOpen,
			setContrastMode,
			toggleContrastMode,
			setIsSettingsModalOpen,
			toggleSettingsModalVisibility,
		],
	);
};
